import React, { useState, useEffect, useCallback } from "react";
import {
  getStatementByAccount,
  getStatementToExport,
  getStatementPDF,
  sendStatementByEmail,
  getAgedBalancesByAccount,
  getReceiptByNumber,
  sendInvoiceByEmail,
  getCreditNoteByNumber,
  updateAccountEdi,
  updateAccountShowOnStatement,
} from "../../afinanceapi";
import { getUserPracticeById, getPracticeById } from "../../api";
import {
  Form,
  Button,
  Row,
  Col,
  Table,
  Container,
  Modal,
  Dropdown,
} from "react-bootstrap";
import BulkExportModal from "./BulkExportModal";
import CreateReceiptModal from "../Accounts/Receipt";
import CreditNote from "./CreditNote";
import WriteOffModal from "./WriteOffModal";
import { jwtDecode } from "jwt-decode";
import { useNavigate } from "react-router-dom";
import "./AccountStatement.css";
import EmailStatementModal from "./EmailStatement";
import { ExportPDFReceipt } from "./ExportReceiptPDF";
import EmailDocumentModal from "./EmailDocumentModal";

export default function AccountStatements({ account }) {
  const [accountId, setAccountId] = useState(null);
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [practiceIds, setPracticeIds] = useState([]);
  const [transactions, setTransactions] = useState([]);
  const [practice, setPractice] = useState(null);
  const [showReceiptModal, setShowReceiptModal] = useState(false);
  const [showCreditNoteModal, setShowCreditNoteModal] = useState(false);
  const [showWriteOffModal, setShowWriteOffModal] = useState(false);
  const [receiptAccount, setReceiptAccount] = useState(null);
  const [creditNoteAccount, setCreditNoteAccount] = useState(null);
  const [statementBlob, setStatementBlob] = useState(null);
  const [agedBalances, setAgedBalances] = useState([]);
  const [statementData, setStatementData] = useState(null);
  const [selectedReceiptEntry, setSelectedReceiptEntry] = useState(null);
  const [showReceiptActionModal, setShowReceiptActionModal] = useState(false);
  const [showStatementEmailModal, setShowStatementEmailModal] = useState(false);
  const [showReceiptEmailModal, setShowReceiptEmailModal] = useState(false);
  const [showBulkExport, setShowBulkExport] = useState(false);
  const [unallocatedBalance, setUnallocatedBalance] = useState(0);
  const [unallocatedReceipts, setUnallocatedReceipts] = useState([]);
  const [showAllUnallocated, setShowAllUnallocated] = useState(false);
  const [showOnStatement, setShowOnStatement] = useState(true);
  const [userRole, setUserRole] = useState(false);
  const [docEmailBlob, setDocEmailBlob] = useState(null); // PDF for receipt or credit-note
  const [docEmailType, setDocEmailType] = useState("receipt"); // "receipt" | "creditNote"
  const [docEmailData, setDocEmailData] = useState(null); // emailData to send to backend
  const [showEdiModal, setShowEdiModal] = useState(false);
  const [ediDisabled, setEdiDisabled] = useState(false);
  const [ediEndDate, setEdiEndDate] = useState("");

  const navigate = useNavigate();

  // Initialize EDI state from account prop
  useEffect(() => {
    if (account) {
      setEdiDisabled(!!account.ediDisabled);
      setEdiEndDate(account.ediEndDate || "");
      setShowOnStatement(account.showOnStatement !== false); // default true
    }
  }, [account]);

  useEffect(() => {
    async function fetchAccountData() {
      if (account && account._id) {
        setAccountId(account._id);
        const token = sessionStorage.getItem("user");
        const user = jwtDecode(token);
        const practiceData = await getUserPracticeById(user._id);
        setPractice(practiceData);
        setPracticeIds(user.practice || []);
        const rolesFromRole = Array.isArray(user?.role)
          ? user.role
          : user?.role
          ? [user.role]
          : [];
        const rolesFromPermissions = Array.isArray(user?.permissions)
          ? user.permissions
          : [];

        const allRoles = [
          ...new Set([...rolesFromRole, ...rolesFromPermissions]),
        ];
        const allowed = new Set(["Finance", "SuperAdmin"]);
        const hasAllowedRole = allRoles.some((r) => allowed.has(r));

        setUserRole(!!hasAllowedRole);

        const todayDate = new Date();
        const formattedEndDate = todayDate.toISOString().split("T")[0];

        const oneMonthBefore = new Date();
        oneMonthBefore.setMonth(todayDate.getMonth() - 1);
        const formattedStartDate = oneMonthBefore.toISOString().split("T")[0];
        setStartDate(formattedStartDate);
        setEndDate(formattedEndDate);
      }
    }
    fetchAccountData();
  }, [account]);

  function formatDate(isoString) {
    return isoString ? isoString.split("T")[0] : "";
  }

  const fetchData = useCallback(async function fetchData() {
    if (!accountId || !startDate || !endDate || practiceIds.length === 0)
      return;

    try {
      const data = await getStatementByAccount(
        accountId,
        startDate,
        endDate,
        practiceIds
      );
      const { invoices = [], openingBalance = 0, closingBalance = 0, unallocatedReceipts: unallocRcpts = [] } = data;
      setUnallocatedReceipts(unallocRcpts);

      const transactions = [];
      // Opening Balance
      transactions.push({
        date: formatDate(startDate),
        description: "Opening Balance",
        invoiceAmount: 0,
        receiptFromPatient: 0,
        receiptFromMedicalAid: 0,
        balance: openingBalance,
      });

      // Process invoices and receipts
      invoices.forEach((inv) => {
        const invoiceAmount = parseFloat(inv.invoiceTotalAmount || 0);

        transactions.push({
          invoiceId: inv._id,
          invoice: inv,
          date: formatDate(inv.invoiceDate),
          description: `Invoice-${inv.invoiceNumber}`,
          status: inv.status,
          invoiceAmount,
          receiptFromPatient: inv.patientTotal,
          receiptFromMedicalAid: inv.medicalAidTotal,
          balance: inv.runningBalance,
        });

        inv.receipts.forEach((r) => {
          const num =
            r.receiptNumber || r.creditNoteNumber || r.refundNumber || r.writeOffNumber;
          const dt =
            r.receiptDate || r.creditNoteDate || r.refundDate || r.writeOffDate;
          const isWriteOff = r.paymentSource === "writeOff";

          let fromPatient = 0;
          let fromMedAid = 0;
          if (isWriteOff) {
            fromPatient = r.writeOffPatientAmount || 0;
            fromMedAid = r.writeOffMedAidAmount || 0;
          } else if (["Patient", "patient"].includes(r.paymentSource)) {
            fromPatient = r.receiptAmount || r.creditNoteAmount || 0;
          } else if (["Medical Aid", "medicalAid"].includes(r.paymentSource)) {
            fromMedAid = r.receiptAmount || r.creditNoteAmount || 0;
          }

          transactions.push({
            receiptNumber: num,
            receiptDate: dt,
            paymentAmount:
              r.receiptAmount || r.creditNoteAmount || r.refundAmount || r.writeOffAmount || 0,
            receiptReference:
              r.receiptReference ||
              r.creditNoteReference ||
              r.refundReference ||
              "",
            practiceNumber: inv.practiceNumber,
            date: "",
            description: `${num} (${dt})`,
            status: "",
            paymentSource: r.paymentSource,
            receiptFromPatient: fromPatient,
            receiptFromMedicalAid: fromMedAid,
          });
        });
      });

      // Closing Balance
      transactions.push({
        date: formatDate(endDate),
        description: "Closing Balance",
        invoiceAmount: 0,
        receiptFromPatient: 0,
        receiptFromMedicalAid: 0,
        balance: closingBalance,
      });

      setTransactions(transactions);

      const agedBalances = await getAgedBalancesByAccount(accountId);
      setAgedBalances(agedBalances.agedBalances);
      setUnallocatedBalance(agedBalances.unallocatedTotal);

    } catch (error) {
      console.error(
        "❌ Error fetching statements:",
        error.response?.data || error.message
      );
    }
  }, [accountId, startDate, endDate, practiceIds]);

  useEffect(() => {
    if (accountId && startDate && endDate && practiceIds.length > 0) {
      fetchData();
    }
  }, [fetchData, accountId, startDate, endDate, practiceIds]);

  function buildReceiptEmailData({ receipt, account, practice }) {
    const practiceNumber = practice?.practiceNumber || "";
    const patientFirstName = account?.accountFullNames || "";
    const patientLastName = account?.accountLastName || "";

    return {
      practiceNumbers: practiceNumber ? [String(practiceNumber)] : [],
      practiceName: practice?.practiceName || "",
      practiceEmail: practice?.practiceEmail || "",
      practicePhone: practice?.practicePhoneNumber || "",
      practiceNumber,
      logoURL: practice?.logoURL || "",

      patientFirstName,
      patientLastName,
      patientName: [patientFirstName, patientLastName]
        .filter(Boolean)
        .join(" "),

      receiptNumber: receipt?.receiptNumber || "",
      receiptDate: receipt?.receiptDate || "",
    };
  }

  function buildCreditNoteEmailData({ creditNote, account, practice }) {
    const practiceNumber = practice?.practiceNumber || "";
    const patientFirstName = account?.accountFullNames || "";
    const patientLastName = account?.accountLastName || "";

    return {
      practiceNumbers: practiceNumber ? [String(practiceNumber)] : [],
      practiceName: practice?.practiceName || "",
      practiceEmail: practice?.practiceEmail || "",
      practicePhone: practice?.practicePhoneNumber || "",
      practiceNumber,
      logoURL: practice?.logoURL || "",

      patientFirstName,
      patientLastName,
      patientName: [patientFirstName, patientLastName]
        .filter(Boolean)
        .join(" "),

      creditNoteNumber: creditNote?.creditNoteNumber || "",
      creditNoteDate: creditNote?.creditNoteDate || "",
    };
  }

  async function exportPDF() {
    try {
      const practiceNumbers = [
        ...new Set(
          transactions
            .map((t) => t.practiceNumber)
            .filter(Boolean)
        ),
      ];

      const blob = await getStatementPDF(accountId, startDate, endDate, practiceNumbers.length > 0 ? practiceNumbers : practiceIds);

      const blobURL = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = blobURL;
      const accountName = account?.accountFullNames || "Account";
      link.download = `Statement_${accountName}_${startDate}_to_${endDate}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(blobURL);
    } catch (error) {
      console.error("Error exporting statement PDF:", error);
      alert("Failed to export PDF. Please try again.");
    }
  }

  async function prepareEmailStatement() {
    try {
      const practiceNumbers = [
        ...new Set(
          transactions
            .map((t) => t.practiceNumber)
            .filter(Boolean)
        ),
      ];

      const blob = await getStatementPDF(accountId, startDate, endDate, practiceNumbers.length > 0 ? practiceNumbers : practiceIds);

      // Still need statement data for the email modal template variables
      const data = await getStatementToExport(
        accountId,
        startDate,
        endDate,
        practiceIds
      );

      setStatementData(data);
      setStatementBlob(blob);
      setShowStatementEmailModal(true);
    } catch (error) {
      console.error("Failed to prepare email statement:", error);
      alert("Failed to prepare email statement. Please try again.");
    }
  }

  const handleCreateInvoice = (e) => {
    e.stopPropagation();
    navigate(`/invoices/1/${account._id}`);
  };

  const handleCreateReceipt = (e) => {
    setReceiptAccount(account);
    setShowReceiptModal(true); // ✅ Show receipt modal
  };
  const handleCreditNote = (e) => {
    setCreditNoteAccount(account);
    setShowCreditNoteModal(true);
  };
  const handleWriteOff = () => {
    setShowWriteOffModal(true);
  };

  async function fetchReceiptData(receiptNumber) {
    const isCreditNote = receiptNumber?.includes("CN-");

    const [receiptDataImported, cnDataImported] = isCreditNote
      ? [null, await getCreditNoteByNumber(receiptNumber)]
      : [await getReceiptByNumber(receiptNumber), null];

    const practiceData = await getPracticeById(
      receiptDataImported?.receiptPracticeNumber ||
        cnDataImported?.creditNotePracticeNumber
    );

    const receiptData = {
      receiptNumber:
        receiptDataImported?.receiptNumber || cnDataImported?.creditNoteNumber,
      receiptDate:
        receiptDataImported?.receiptDate || cnDataImported?.creditNoteDate,
      receiptTotalAmount:
        receiptDataImported?.receiptTotalAmount ||
        cnDataImported?.creditNoteTotalAmount ||
        0,
      receiptInvoices:
        receiptDataImported?.receiptInvoices ||
        cnDataImported?.creditNoteInvoiceLines ||
        [],
      receiptPaymentMethod:
        receiptDataImported?.receiptMethod ||
        cnDataImported?.creditNotePaymentMethod ||
        "",
      receiptReference:
        receiptDataImported?.receiptInvoices?.[0]?.receiptReference ||
        cnDataImported?.creditNoteReference ||
        "",
      practice: practiceData,
      account,
    };

    return { receiptData, practiceData, isCreditNote };
  }

  async function handleDownloadClicked() {
    if (!selectedReceiptEntry) return;

    const { receiptData } = await fetchReceiptData(selectedReceiptEntry.receiptNumber);
    await ExportPDFReceipt(receiptData);
    setShowReceiptActionModal(false);
  }

  async function handleSendEmail() {
    if (!selectedReceiptEntry) return;

    try {
      const { receiptData, practiceData, isCreditNote } = await fetchReceiptData(
        selectedReceiptEntry.receiptNumber
      );

      const blob = await ExportPDFReceipt(receiptData, { asBlob: true });

      const emailVars = isCreditNote
        ? buildCreditNoteEmailData({
            creditNote: {
              creditNoteNumber: receiptData.receiptNumber,
              creditNoteDate: receiptData.receiptDate,
            },
            account,
            practice: practiceData,
          })
        : buildReceiptEmailData({
            receipt: {
              receiptNumber: receiptData.receiptNumber,
              receiptDate: receiptData.receiptDate,
            },
            account,
            practice: practiceData,
          });

      setDocEmailBlob(blob);
      setDocEmailType(isCreditNote ? "creditNote" : "receipt");
      setDocEmailData(emailVars);
      setShowReceiptEmailModal(true);
      setShowReceiptActionModal(false);
    } catch (error) {
      console.error("Failed to prepare receipt/credit note email:", error);
      alert("Failed to prepare document for email. Please try again.");
    }
  }

  async function handleSaveEdi() {
    try {
      await updateAccountEdi(account._id, ediDisabled, ediEndDate);
      setShowEdiModal(false);
    } catch (error) {
      console.error("Failed to save EDI settings:", error);
      alert("Failed to save EDI settings.");
    }
  }

  const hasMedicalAid = account?.accountMedicalAid && account.accountMedicalAid !== "";
  const ediActive = ediDisabled && ediEndDate && new Date(ediEndDate) >= new Date(new Date().toISOString().split("T")[0]);

  const hasWrittenOff = transactions.some((t) => t.status === "Written Off");
  const canNavigate = userRole;

  return (
    <div>
      <Container fluid className="mb-2 px-4">
        {" "}
        {/* <-- Consistent horizontal padding */}
        <Row className="align-items-center justify-content-between mb-2">
          <Col md={4}>
            <h5 className="text-primary mb-0">Account Statement</h5>
          </Col>

          <Col md={8} className="d-flex justify-content-end align-items-center gap-2">
            {hasWrittenOff && (
              <span className="badge bg-danger py-2 px-3">
                <i className="fa fa-ban me-1" />
                Written Off
              </span>
            )}
            {hasMedicalAid && (
              <Button
                variant={ediActive ? "warning" : "outline-secondary"}
                style={ediActive ? { whiteSpace: "nowrap" } : { width: "150px" }}
                onClick={() => setShowEdiModal(true)}
              >
                <i className="fa-solid fa-tower-broadcast me-1" />
                {ediActive ? `No EDI until ${ediEndDate}` : "EDI"}
              </Button>
            )}
            <Dropdown>
              <Dropdown.Toggle
                variant="primary"
                style={{ width: "150px" }}
                id="statement-actions-dropdown"
              >
                <i className="fa-solid fa-gears me-1" />
                Actions
              </Dropdown.Toggle>

              <Dropdown.Menu align="end" className="shadow-sm">
                {userRole && (
                  <>
                    <Dropdown.Item onClick={handleCreateInvoice}>
                      <i className="fa fa-file-invoice-dollar me-2" />
                      Create Invoice
                    </Dropdown.Item>
                    <Dropdown.Item onClick={handleCreateReceipt}>
                      <i className="fa fa-receipt me-2" />
                      Receipt
                    </Dropdown.Item>
                    <Dropdown.Item onClick={handleCreditNote}>
                      <i className="fa fa-undo me-2" />
                      Credit Note
                    </Dropdown.Item>
                    <Dropdown.Item onClick={handleWriteOff}>
                      <i className="fa fa-ban me-2" />
                      Write Off
                    </Dropdown.Item>
                    <Dropdown.Divider />
                  </>
                )}
                <Dropdown.Item
                  onClick={exportPDF}
                  disabled={!accountId || !startDate || !endDate}
                >
                  <i className="fa fa-download me-2" />
                  Download PDF
                </Dropdown.Item>
                <Dropdown.Item
                  onClick={prepareEmailStatement}
                  disabled={!accountId || !startDate || !endDate}
                >
                  <i className="fa-regular fa-envelope me-2" />
                  Email Statement
                </Dropdown.Item>
                <Dropdown.Item onClick={() => setShowBulkExport(true)}>
                  <i className="fa-solid fa-file-export me-2" />
                  Bulk Export
                </Dropdown.Item>
              </Dropdown.Menu>
            </Dropdown>
          </Col>
        </Row>
        <Row>
          <Col md={2}>
            <Form>
              <Form.Group className="mb-2">
                <Form.Label>Start Date</Form.Label>
                <Form.Control
                  type="date"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                />
              </Form.Group>

              <Form.Group className="mb-2">
                <Form.Label>End Date</Form.Label>
                <Form.Control
                  type="date"
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                />
              </Form.Group>
            </Form>
          </Col>

          <Col md={10}>
            <Table bordered hover className="mt-1">
              <thead className="table-primary text-center text-primary">
                <tr>
                  <th style={{ width: "10%" }} className="text-primary">
                    Date
                  </th>
                  <th style={{ width: "25%" }} className="text-primary">
                    Description
                  </th>
                  <th style={{ width: "10%" }} className="text-primary">
                    Status
                  </th>
                  <th style={{ width: "15%" }} className="text-primary">
                    Invoice | Receipt
                  </th>
                  <th style={{ width: "15%" }} className="text-primary">
                    Patient
                  </th>
                  <th style={{ width: "15%" }} className="text-primary">
                    Medical Aid
                  </th>
                  <th style={{ width: "15%" }} className="text-primary">
                    Balance
                  </th>
                </tr>
              </thead>
              <tbody>
                {transactions.map((entry, index) => {
                  // ——— Computed once per entry ———
                  const isPending = entry.status === "Pending";

                  const isReceiptEntry =
                    entry.description?.includes("Receipt") ||
                    ((entry.description?.includes("RCT-") ||
                      entry.description?.includes("CN-") || entry.description?.includes("WO-")) &&
                      !entry.invoiceId &&
                      entry.description !== "Opening Balance" &&
                      entry.description !== "Closing Balance");

                  const isRefundEntry = entry.description?.includes("RF-");
                  const isWriteOffEntry = entry.description?.includes("WO-");
                  const isSubEntry = isReceiptEntry || isRefundEntry || isWriteOffEntry;

                  // ——— Render ———
                  return (
                    <tr
                      key={`entry-${index}`}
                      onClick={() => {
                        if (!canNavigate) return;
                        if (isReceiptEntry) {
                          setSelectedReceiptEntry({
                            receiptNumber: entry.receiptNumber,
                          });
                          setShowReceiptActionModal(true);
                        } else if (!isWriteOffEntry && entry.invoice) {
                          const specialStatuses = ["Draft", "Pending"];
                          const path = specialStatuses.includes(entry.status)
                            ? `/invoices/${entry.invoice._id}/${account._id}`
                            : `/invoices/posted/${entry.invoice._id}`;
                          navigate(
                            path,
                            specialStatuses.includes(entry.status)
                              ? { state: { account } }
                              : undefined
                          );
                        }
                      }}
                      style={{
                        cursor:
                          canNavigate && (isReceiptEntry || (!isWriteOffEntry && entry.invoice))
                            ? "pointer"
                            : "default",
                      }}
                      title={
                        canNavigate
                          ? isReceiptEntry || entry.invoice
                            ? "Open details"
                            : ""
                          : "Insufficient permissions"
                      }
                    >
                      {/* amounts stay exactly as-is */}

                      <td>{entry.date || ""}</td>

                      <td>
                        {isSubEntry ? (
                          <span className="ms-3">{entry.description}</span>
                        ) : isPending ? (
                          <span>{entry.description}</span>
                        ) : (
                          <strong>{entry.description}</strong>
                        )}
                      </td>

                      <td>
                        {isPending ? (
                          <em>{entry.status}</em>
                        ) : (
                          <strong>{entry.status}</strong>
                        )}
                      </td>

                      <td className="text-center">
                        {isRefundEntry
                          ? `R ${parseFloat(entry.paymentAmount || 0).toFixed(
                              2
                            )}`
                          : entry.invoiceAmount
                          ? `R ${entry.invoiceAmount.toFixed(2)}`
                          : ""}
                      </td>

                      <td className="text-center">
                        {(isReceiptEntry || isWriteOffEntry) && entry.receiptFromPatient
                          ? `(R ${parseFloat(entry.receiptFromPatient).toFixed(
                              2
                            )})`
                          : ""}
                      </td>

                      <td className="text-center">
                        {(isReceiptEntry || isWriteOffEntry) && entry.receiptFromMedicalAid
                          ? `(R ${parseFloat(
                              entry.receiptFromMedicalAid
                            ).toFixed(2)})`
                          : ""}
                      </td>

                      <td className={`text-end${isPending ? "" : " fw-bold"}`}>
                        {entry.balance != null
                          ? `R ${entry.balance.toFixed(2)}`
                          : ""}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </Table>

            {/* Unallocated Receipts — only shown when there are unallocated funds */}
            {unallocatedReceipts.length > 0 && (
              <>
                <div className="d-flex align-items-center gap-2 mb-1">
                  <Form.Check
                    type="checkbox"
                    id="showOnStatement"
                    label="Show unallocated funds on statement"
                    checked={showOnStatement}
                    onChange={async (e) => {
                      const newVal = e.target.checked;
                      setShowOnStatement(newVal);
                      try {
                        await updateAccountShowOnStatement(account._id, newVal);
                      } catch (err) {
                        console.error("Failed to save show on statement preference:", err);
                        setShowOnStatement(!newVal); // revert on failure
                      }
                    }}
                  />
                </div>
                <div className="d-flex align-items-center justify-content-between mb-1">
                  <h6 className="mb-0 text-primary fw-bold">Unallocated Receipts</h6>
                  <Button
                    variant="outline-secondary"
                    size="sm"
                    onClick={() => setShowAllUnallocated((v) => !v)}
                  >
                    {showAllUnallocated ? "Hide fully used" : "Show all"}
                  </Button>
                </div>
                <Table bordered size="sm">
                  <thead className="table-primary text-center">
                    <tr>
                      <th className="text-primary">Date</th>
                      <th className="text-primary text-end">Received</th>
                      <th className="text-primary text-end">Medical Aid</th>
                      <th className="text-primary text-end">Patient</th>
                      <th className="text-primary text-end">Used</th>
                      <th className="text-primary text-end">Balance</th>
                    </tr>
                  </thead>
                  <tbody>
                    {unallocatedReceipts
                      .filter((r) => {
                        const balance = (r.paymentAmount || 0) - (r.usedAmount || 0);
                        return showAllUnallocated || balance > 0;
                      })
                      .map((r, idx) => {
                        const received = r.paymentAmount || 0;
                        const used = r.usedAmount || 0;
                        const balance = received - used;
                        const isPatient = ["Patient", "patient"].includes(r.paymentSource);
                        const isMedAid = ["Medical Aid", "medicalAid"].includes(r.paymentSource);
                        return (
                          <tr key={`unalloc-${idx}`}>
                            <td>{formatDate(r.receiptDate)}</td>
                            <td className="text-end">R {received.toFixed(2)}</td>
                            <td className="text-end">{isMedAid ? `R ${received.toFixed(2)}` : ""}</td>
                            <td className="text-end">{isPatient ? `R ${received.toFixed(2)}` : ""}</td>
                            <td className="text-end">R {used.toFixed(2)}</td>
                            <td className="text-end fw-bold">R {balance.toFixed(2)}</td>
                          </tr>
                        );
                      })}
                  </tbody>
                </Table>
              </>
            )}

            <div className="d-flex justify-content-end mt-4">
              <div style={{ width: "60%" }}>
                {" "}
                {/* Adjust this width as needed */}
                <Table bordered size="sm">
                  <thead className="table-primary text-center text-primary">
                    <tr>
                      <th className="text-primary"></th>
                      <th className="text-primary">Outstanding</th>
                      <th className="text-primary">30 Days</th>
                      <th className="text-primary">60 Days</th>
                      <th className="text-primary">90 Days</th>
                      <th className="text-primary">120+ Days</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td className="fw-bold">Patient</td>
                      <td className="text-end text-danger fw-bold">
                        R{" "}
                        {(
                          (agedBalances.days30?.patient || 0) +
                          (agedBalances.days60?.patient || 0) +
                          (agedBalances.days90?.patient || 0) +
                          (agedBalances.days120plus?.patient || 0)
                        ).toFixed(2)}
                      </td>
                      <td className="text-end">
                        R {agedBalances.days30?.patient.toFixed(2)}
                      </td>
                      <td className="text-end">
                        R {agedBalances.days60?.patient.toFixed(2)}
                      </td>
                      <td className="text-end">
                        R {agedBalances.days90?.patient.toFixed(2)}
                      </td>
                      <td className="text-end">
                        R {agedBalances.days120plus?.patient.toFixed(2)}
                      </td>
                    </tr>
                    <tr>
                      <td className="fw-bold">Medical Aid</td>
                      <td className="text-end text-danger fw-bold">
                        R{" "}
                        {(
                          (agedBalances.days30?.medicalAid || 0) +
                          (agedBalances.days60?.medicalAid || 0) +
                          (agedBalances.days90?.medicalAid || 0) +
                          (agedBalances.days120plus?.medicalAid || 0)
                        ).toFixed(2)}
                      </td>
                      <td className="text-end">
                        R {agedBalances.days30?.medicalAid.toFixed(2)}
                      </td>
                      <td className="text-end">
                        R {agedBalances.days60?.medicalAid.toFixed(2)}
                      </td>
                      <td className="text-end">
                        R {agedBalances.days90?.medicalAid.toFixed(2)}
                      </td>
                      <td className="text-end">
                        R {agedBalances.days120plus?.medicalAid.toFixed(2)}
                      </td>
                    </tr>
                    <tr>
                      <td className="fw-bold">Unallocated Funds</td>
                      <td className="fw-bold text-success text-end">
                        R {unallocatedBalance.toFixed(2)}
                      </td>
                      <td></td>
                      <td></td>
                      <td></td>
                      <td></td>
                    </tr>
                  </tbody>
                </Table>
                <Modal
                  show={showReceiptActionModal}
                  onHide={() => setShowReceiptActionModal(false)}
                  centered
                >
                  <Modal.Header closeButton>
                    <Modal.Title>Receipt Options</Modal.Title>
                  </Modal.Header>
                  <Modal.Body>
                    <p>Would you like to download or email this receipt?</p>
                  </Modal.Body>
                  <Modal.Footer>
                    <Button variant="primary" onClick={handleDownloadClicked}>
                      Download
                    </Button>
                    <Button
                      variant="outline-secondary"
                      onClick={handleSendEmail}
                    >
                      Email
                    </Button>
                    <Button
                      variant="outline-secondary"
                      onClick={() => setShowReceiptActionModal(false)}
                    >
                      Cancel
                    </Button>
                  </Modal.Footer>
                </Modal>
                <EmailDocumentModal
                  show={showReceiptEmailModal}
                  onHide={() => setShowReceiptEmailModal(false)}
                  title={
                    docEmailType === "creditNote"
                      ? "Email Credit Note"
                      : "Email Receipt"
                  }
                  defaultEmail={account?.accountEmail}
                  email2={account?.accountEmail2}
                  email3={account?.accountEmail3}
                  email4={account?.accountEmail4}
                  email5={account?.accountEmail5}
                  helperText="The document will be sent as a PDF attachment to all email addresses entered above."
                  onSend={(emails) => {
                    if (!docEmailBlob) return;

                    // snapshot current state
                    const blob = docEmailBlob;
                    const edata = docEmailData;
                    const dtype = docEmailType; // "receipt" | "creditNote"
                    const acctId = account?._id;

                    // close + reset immediately
                    setShowReceiptEmailModal(false);
                    setDocEmailBlob(null);
                    setDocEmailData(null);

                    // fire-and-forget
                    return sendInvoiceByEmail({
                      emails, // ✅ multi email list
                      pdfBlob: blob,
                      type: dtype,
                      accountId: acctId,
                      emailData: edata,
                    });
                  }}
                />
              </div>
            </div>
          </Col>
        </Row>
      </Container>
      <BulkExportModal
        show={showBulkExport}
        onHide={() => setShowBulkExport(false)}
        accountId={accountId}
      />
      {showCreditNoteModal && creditNoteAccount && (
        <CreditNote
          show={showCreditNoteModal}
          onHide={() => setShowCreditNoteModal(false)}
          account={creditNoteAccount}
        />
      )}

      {showWriteOffModal && (
        <WriteOffModal
          show={showWriteOffModal}
          onHide={() => setShowWriteOffModal(false)}
          account={account}
        />
      )}

      {showReceiptModal && receiptAccount && (
        <CreateReceiptModal
          show={showReceiptModal}
          onHide={() => setShowReceiptModal(false)}
          account={receiptAccount}
        />
      )}

      <Modal show={showEdiModal} onHide={() => setShowEdiModal(false)} centered size="sm">
        <Modal.Header closeButton>
          <Modal.Title>Set EDI</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form.Group className="mb-3">
            <Form.Check
              type="switch"
              id="edi-disabled-switch"
              label="Disable EDI"
              checked={ediDisabled}
              onChange={(e) => setEdiDisabled(e.target.checked)}
            />
          </Form.Group>
          {ediDisabled && (
            <Form.Group className="mb-3">
              <Form.Label>Disable until</Form.Label>
              <Form.Control
                type="date"
                value={ediEndDate}
                onChange={(e) => setEdiEndDate(e.target.value)}
              />
            </Form.Group>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="outline-secondary" onClick={() => setShowEdiModal(false)}>
            Cancel
          </Button>
          <Button variant="primary" onClick={handleSaveEdi} disabled={ediDisabled && !ediEndDate}>
            Save
          </Button>
        </Modal.Footer>
      </Modal>

      {showStatementEmailModal && statementBlob && (
        <EmailStatementModal
          show={showStatementEmailModal}
          onHide={() => setShowStatementEmailModal(false)}
          defaultEmail={account.accountEmail}
          email2={account.accountEmail2}
          email3={account.accountEmail3}
          email4={account.accountEmail4}
          email5={account.accountEmail5}
          statementData={statementData}
          pdfBlob={statementBlob}
          sendStatementByEmail={sendStatementByEmail}
        />
      )}
    </div>
  );
}
